// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Grid, Loader, ScrollArea, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import type { DatePickerProps } from '@mantine/dates';
import { DatePicker } from '@mantine/dates';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import type { JSX } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { type IntakeAppointmentCandidate, useIntakeSlots } from '../../hooks/useIntakeSlots';
import { formatAppointmentTime } from '../../utils/appointment-formatting';

export type IntakeSelection = IntakeAppointmentCandidate;

interface IntakeSlotPickerProps {
  uniqueId: string;
  selectedSlot: IntakeSelection | undefined;
  onSlotSelected: (slot: IntakeSelection, appointmentType: AppointmentTypeDefinition) => void;
}

/**
 * Pre-auth slot picker for the new-patient intake appointment. Visual layout mirrors
 * `SlotPicker` from the authenticated booking flow; data is fetched via the public
 * `find-intake-slots` bot using the patient's onboarding `uniqueId`.
 */
export function IntakeSlotPicker({ uniqueId, selectedSlot, onSlotSelected }: IntakeSlotPickerProps): JSX.Element {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [visibleMonth, setVisibleMonth] = useState<Date>(new Date());

  const startDate = selectedDate ? toISODateString(selectedDate) : undefined;
  const endDate = selectedDate ? toISODateString(addDays(selectedDate, 1)) : undefined;

  const { appointments, appointmentType, loading, error } = useIntakeSlots({
    uniqueId,
    fromDate: startDate,
    toDate: endDate,
  });

  const monthStart = toISODateString(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1));
  const monthEnd = toISODateString(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1));
  const { appointments: monthAppointments, loading: monthLoading } = useIntakeSlots({
    uniqueId,
    fromDate: monthStart,
    toDate: monthEnd,
  });

  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    for (const candidate of monthAppointments) {
      const d = new Date(candidate.appointment.start ?? '');
      dates.add(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      );
    }
    return dates;
  }, [monthAppointments]);

  const minDate = addDays(new Date(), 2);

  const getDayProps: DatePickerProps['getDayProps'] = useCallback(
    (date: string) => {
      const d = new Date(date + 'T00:00:00');
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      const isSelected =
        selectedDate &&
        d.getFullYear() === selectedDate.getFullYear() &&
        d.getMonth() === selectedDate.getMonth() &&
        d.getDate() === selectedDate.getDate();

      if (isSelected) {
        return {
          style: {
            backgroundColor: 'var(--mantine-color-polaris-5)',
            color: 'white',
            borderRadius: 'var(--mantine-radius-sm)',
          },
        };
      }

      if (availableDates.has(key)) {
        return {
          style: {
            backgroundColor: 'var(--mantine-color-polaris-0)',
            borderRadius: 'var(--mantine-radius-sm)',
          },
        };
      }

      return {};
    },
    [availableDates, selectedDate]
  );

  const displayedAppointments: IntakeSelection[] = appointments.filter((candidate) =>
    isSameLocalDate(new Date(candidate.appointment.start ?? ''), selectedDate)
  );

  const formattedSelectedDate = selectedDate
    ? selectedDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';
  const searchState = monthLoading ? 'loading' : 'ready';

  return (
    <Stack gap="md" data-v2-scheduling-search={searchState}>
      <Title order={3}>Available Appointments</Title>
      <Text size="sm" c="dimmed">
        Choose a date and time.
      </Text>
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Box
            p="md"
            style={{
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 'var(--mantine-radius-md)',
              position: 'relative',
            }}
          >
            <DatePicker
              value={selectedDate}
              onChange={(value: string | Date | null) => {
                if (!value) {
                  setSelectedDate(null);
                } else if (typeof value === 'string') {
                  const [year, month, day] = value.split('-').map(Number);
                  setSelectedDate(new Date(year, month - 1, day));
                } else {
                  setSelectedDate(new Date(value.getFullYear(), value.getMonth(), value.getDate()));
                }
              }}
              onDateChange={(value: string) => {
                const [year, month] = value.split('-').map(Number);
                setVisibleMonth(new Date(year, month - 1, 1));
              }}
              getDayProps={getDayProps}
              minDate={minDate}
              maxDate={addDays(new Date(), 60)}
              nextLabel="Next month"
              previousLabel="Previous month"
              size="lg"
              styles={{
                calendarHeader: { maxWidth: '100%' },
                month: { width: '100%' },
                day: {
                  width: '100%',
                  height: 48,
                  fontSize: 'var(--mantine-font-size-md)',
                  borderRadius: 'var(--mantine-radius-sm)',
                },
              }}
            />
            {monthLoading && (
              <Box
                role="status"
                aria-label="Loading available appointments"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.72)',
                  borderRadius: 'var(--mantine-radius-md)',
                  zIndex: 1,
                }}
              >
                <Stack align="center" gap="xs">
                  <Loader size="md" />
                  <Text size="sm" c="dimmed">
                    Loading availability…
                  </Text>
                </Stack>
              </Box>
            )}
          </Box>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          {!selectedDate && (
            <Stack align="center" justify="center" h="100%" py="xl">
              <Text c="dimmed" size="sm" ta="center">
                Select a date to see available times.
              </Text>
            </Stack>
          )}

          {selectedDate && (
            <Stack gap="sm">
              <Text size="sm" c="dimmed" ta="center">
                Available appointments for
              </Text>
              <Text fw={600} ta="center" size="md">
                {formattedSelectedDate}
              </Text>

              {loading && (
                <Stack align="center" py="xl">
                  <Loader size="sm" />
                </Stack>
              )}

              {error && (
                <Text c="red" size="sm" ta="center">
                  {error.message}
                </Text>
              )}

              {!loading && displayedAppointments.length === 0 && (
                <Text c="dimmed" size="sm" ta="center" py="xl">
                  No available times on this date. Try another date.
                </Text>
              )}

              {!loading && displayedAppointments.length > 0 && appointmentType && (
                <ScrollArea h={400} offsetScrollbars>
                  <Stack gap="xs">
                    {displayedAppointments.map((slot) => {
                      const start = slot.appointment.start ?? '';
                      const end = slot.appointment.end ?? '';
                      const selectionKey = slot.key;
                      let selectedKey: string | undefined;
                      if (selectedSlot) {
                        selectedKey = selectedSlot.key;
                      }
                      const isSelected = selectedKey === selectionKey;
                      return (
                        <UnstyledButton
                          key={selectionKey}
                          onClick={() => onSlotSelected(slot, appointmentType)}
                          py="sm"
                          px="md"
                          style={{
                            borderRadius: 'var(--mantine-radius-md)',
                            border: `2px solid ${isSelected ? 'var(--mantine-color-polaris-5)' : 'var(--mantine-color-polaris-1)'}`,
                            backgroundColor: isSelected
                              ? 'var(--mantine-color-polaris-5)'
                              : 'var(--mantine-color-polaris-0)',
                            color: isSelected ? 'white' : 'inherit',
                            textAlign: 'center',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <Text size="sm" fw={500}>
                            {formatAppointmentTime(start, end)}
                          </Text>
                          <Text size="xs" c={isSelected ? 'white' : 'dimmed'}>
                            {slot.providerName}
                          </Text>
                        </UnstyledButton>
                      );
                    })}
                  </Stack>
                </ScrollArea>
              )}
            </Stack>
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function toISODateString(date: Date): string {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString();
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function isSameLocalDate(left: Date, right: Date | null): boolean {
  return Boolean(
    right &&
      left.getFullYear() === right.getFullYear() &&
      left.getMonth() === right.getMonth() &&
      left.getDate() === right.getDate()
  );
}
